import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { ChatsCircle, Users, MegaphoneSimple, CalendarBlank, TrendUp, Robot, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { LineChart, Line, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import ChartFrame from "@/components/ChartFrame";

const STAGE_KEYS = ["nuevo", "contactado", "calificado", "negociacion", "ganado", "perdido"];

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6 hover:border-[#3F3F46] transition-colors" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="flex items-start justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</div>
      <Icon size={16} className="text-[#71717A]" weight="regular" />
    </div>
    <div className="mt-6 flex items-baseline gap-2">
      <span className="font-display text-3xl font-light tracking-tighter text-white">{value}</span>
      {accent && <span className="text-xs text-[#10B981]">{accent}</span>}
    </div>
    {sub && <div className="text-xs text-[#71717A] mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [convos, setConvos] = useState([]);
  const dateLocale = lang === "en" ? "en-US" : "es-MX";

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/conversations").then((r) => setConvos(r.data.slice(0, 5))).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="p-8">
        <div className="text-[#A1A1AA] text-sm">{t("dash_loading")}</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">{t("dash_kicker")}</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">{t("dash_title")}</h1>
        </div>
        <div className="text-xs text-[#71717A] font-mono">{new Date().toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label={t("dash_contacts")} value={data.total_contacts} icon={Users} sub={`${data.leads} ${t("dash_contacts_sub")}`} />
        <Stat label={t("dash_messages")} value={data.total_messages} icon={ChatsCircle} sub={`${data.ai_messages} ${t("dash_messages_sub")}`} />
        <Stat label={t("dash_conversion")} value={`${data.conversion_rate}%`} icon={TrendUp} sub={`${data.won} ${t("dash_conversion_sub")}`} accent={data.conversion_rate > 0 ? "+" : ""} />
        <Stat label={t("dash_appointments")} value={data.appointments} icon={CalendarBlank} sub={t("dash_appointments_sub")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{t("dash_activity")}</div>
              <h3 className="font-display text-xl font-medium tracking-tight mt-1">{t("dash_activity_title")}</h3>
            </div>
          </div>
          <ChartFrame height={224}>
            {({ width, height }) => (
              <LineChart data={data.messages_series} width={width} height={height}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickFormatter={(d) => new Date(d).toLocaleDateString(dateLocale, { day: "numeric", month: "short" })} />
                <YAxis stroke="#71717A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="messages" stroke="#5E6AD2" strokeWidth={2} dot={{ fill: "#5E6AD2", r: 3 }} />
              </LineChart>
            )}
          </ChartFrame>
        </div>

        <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{t("dash_funnel")}</div>
          <h3 className="font-display text-xl font-medium tracking-tight mb-5">{t("dash_pipeline")}</h3>
          <div className="space-y-3">
            {STAGE_KEYS.map((k) => {
              const v = data.funnel[k] || 0;
              const max = Math.max(...Object.values(data.funnel || {1: 1}), 1);
              const pct = (v / max) * 100;
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#A1A1AA]">{t(`stage_${k}`)}</span>
                    <span className="font-mono text-white">{v}</span>
                  </div>
                  <div className="h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                    <div className="h-full bg-[#5E6AD2] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{t("dash_recent")}</div>
            <h3 className="font-display text-xl font-medium tracking-tight mt-1">{t("dash_active_convos")}</h3>
          </div>
          <Link to="/app/inbox" className="text-xs text-[#5E6AD2] hover:underline flex items-center gap-1" data-testid="link-inbox">
            {t("dash_view_inbox")} <ArrowUpRight size={12} />
          </Link>
        </div>
        {convos.length === 0 ? (
          <div className="text-sm text-[#71717A] py-8 text-center">
            {t("dash_no_convos")} <button className="text-[#5E6AD2] hover:underline" onClick={() => api.post("/seed-demo").then(() => window.location.reload())} data-testid="dashboard-seed-button">{t("seed_demo")}</button>
          </div>
        ) : (
          <div className="space-y-1">
            {convos.map((c) => (
              <Link key={c.contact.id} to={`/app/inbox?contact=${c.contact.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#18181B] transition-colors group">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-xs font-medium">
                  {c.contact.name[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate">{c.contact.name}</span>
                    <span className="text-[10px] text-[#71717A] font-mono">
                      {c.last_at ? new Date(c.last_at).toLocaleTimeString(dateLocale, { hour: "2-digit", minute: "2-digit" }) : "-"}
                    </span>
                  </div>
                  <div className="text-xs text-[#71717A] truncate">
                    {c.last_message?.sender === "ai" && <Robot size={10} className="inline mr-1 text-[#5E6AD2]" weight="fill" />}
                    {c.last_message?.text || "-"}
                  </div>
                </div>
                {c.unread_count > 0 && (
                  <span className="text-[10px] bg-[#5E6AD2] text-white px-1.5 py-0.5 rounded-full font-mono">{c.unread_count}</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
